import React from "react";
import cns from "classnames";
import moment from "moment";
import { makeStyles, Grid, Typography } from "@material-ui/core";
import { ThemeType } from "@theme";
import { Text, SimpleSelect, Date, Select } from "@components/formx";
import locale from "@bonliva-crm/locale";
import { Options } from "@components/formx/Select";
import { MuiAutocomplete } from "@bonliva-crm/ui";
import FormField from "@modules/formx-react/FormField";

import { TravelRequestRide as TravelRequestRideData } from "./TravelRequest";
import { swedishCities, TravelRequestTime } from "@bonliva-crm/shared";

type Props = {
  className?: string;
  title?: string;
  name: string;
  data: TravelRequestRideData | null;
  countries: Options;
  disabled?: { date?: boolean };
};

const swedishCitiesOptions = swedishCities.toLabelValue((x) => x);

export function TravelRequestRide({
  name,
  data,
  countries,
  className,
  title,
  disabled
}: Props) {
  const classes = useStyles({});
  const getName = (el) => `${name}.${el}`;

  const isSwedish = data?.country === "Sweden";
  const isDateDisabled = disabled?.date ?? false;

  return (
    <div className={cns(classes.root, className)}>
      {title ? (
        <Typography variant="h6" className={classes.title}>
          {title}
        </Typography>
      ) : null}

      <Grid container spacing={1}>
        <Grid item xs={6}>
          <Select
            name={getName("country")}
            label={locale.travelRequest.country}
            options={countries}
            required
          />
        </Grid>
        <Grid item xs={6}>
          {isSwedish ? (
            <FormField name={getName("city")}>
              {(field) => (
                <MuiAutocomplete
                  {...field.fieldProps}
                  inputLabel={field.error || locale.travelRequest.city}
                  error={field.invalid}
                  required
                  options={swedishCitiesOptions}
                  creatable
                />
              )}
            </FormField>
          ) : (
            <Text
              name={getName("city")}
              label={locale.travelRequest.city}
              TextFieldProps={{ required: true }}
            />
          )}
        </Grid>
      </Grid>

      <Grid container spacing={1} className={classes.secondRow}>
        <Grid item xs={6}>
          <Date
            name={getName("date")}
            label={locale.travelRequest.date}
            disabled={isDateDisabled}
            DatePickerProps={{
              minDate: moment().toDate(),
              required: !isDateDisabled
            }}
          />
        </Grid>

        <Grid item xs={6}>
          <SimpleSelect
            label={locale.travelRequest.time}
            name={getName("time")}
            clearable
            options={[
              TravelRequestTime.Morning,
              TravelRequestTime.Day,
              TravelRequestTime.Noon,
              TravelRequestTime.Evening
            ].toLabelValue((x) => locale.travelRequestTime[x])}
          />
        </Grid>

        <Grid item xs={12}>
          <Text
            label={locale.Comment}
            name={getName("comment")}
            TextFieldProps={{
              multiline: true
            }}
          />
        </Grid>
      </Grid>
    </div>
  );
}

const useStyles = makeStyles((theme: ThemeType) => ({
  root: {
    border: `1px solid ${theme.separatorColor}`,
    borderRadius: 4
  },
  title: {
    fontSize: "1.3rem",
    fontWeight: 600,
    textAlign: "center",
    padding: "8px 16px"
  },
  secondRow: {
    paddingTop: 12
  }
}));
